/**
 * Utility functions for handling SSO authentication completion flows
 * (SAML form submission and OAuth form_post).
 */

import type { NavigateFunction } from 'react-router-dom';
import { pendingAuthService } from '@/services/api-clients';
import {
  extractOidcSskData,
  type OidcSskCompletionData,
} from '@/utils/oidc-ssk-utils';
import { makeFullPath } from '@/utils/path-utils';

export interface SplitSamlCompletionData {
  samlResponseB64: string;
  acsUrl: string;
  relayState: string;
  signedInfoHash: string;
  appId: string;
  appName: string;
  supportEmail: string;
}

// The signed-in username comes from the login flow, not from the completion page.
export type SskSigningState = SsoCompletion['data'] & { username: string };

export type SsoCompletion =
  | { kind: 'split_saml'; data: SplitSamlCompletionData }
  | { kind: 'split_oidc'; data: OidcSskCompletionData };

export function navigateToSplitSigning(
  navigate: NavigateFunction,
  completion: SsoCompletion,
  username = ''
): void {
  const path =
    completion.kind === 'split_oidc'
      ? '/oidc-ssk/complete-signing'
      : '/saml-ssk/complete-signing';
  const state: SskSigningState = { ...completion.data, username };
  navigate(makeFullPath(path), { state });
}

/**
 * Extract split SAML data from an HTML string returned by loadAuth().
 * Returns null if the HTML does not contain split SAML data.
 */
function extractSplitSamlData(
  htmlContent: string
): SplitSamlCompletionData | null {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const dataElement = doc.getElementById('saml-ssk-data');

  if (!dataElement) {
    return null;
  }

  return {
    samlResponseB64: dataElement.dataset.samlResponse ?? '',
    acsUrl: dataElement.dataset.acsUrl ?? '',
    relayState: dataElement.dataset.relayState ?? '',
    signedInfoHash: dataElement.dataset.signedInfoHash ?? '',
    appId: dataElement.dataset.appId ?? '',
    appName: dataElement.dataset.appName ?? '',
    supportEmail: dataElement.dataset.supportEmail ?? '',
  };
}

/**
 * Find the auto-submit form in the HTML response from loadAuth().
 * Supports both SAML (id="samlForm") and OAuth form_post (id="oauthForm").
 */
function findAutoSubmitForm(container: HTMLElement): HTMLFormElement | null {
  return (
    (container.querySelector('#samlForm') as HTMLFormElement) ??
    (container.querySelector('#oauthForm') as HTMLFormElement)
  );
}

// OAuth code flow completes via a meta-refresh "Redirecting" page, not a form.
function extractRedirectUrl(doc: Document): string | null {
  const content =
    doc
      .querySelector('meta[http-equiv="refresh" i]')
      ?.getAttribute('content') ?? '';
  const match = /url=(.+)$/i.exec(content);

  return match ? match[1].trim() : null;
}

export function executeCompletionHtml(htmlContent: string): void {
  // Parse detached first so a formless response never overwrites document.body.
  const parsed = new DOMParser().parseFromString(htmlContent, 'text/html');

  if (findAutoSubmitForm(parsed.body)) {
    document.body.innerHTML = htmlContent;
    const form = findAutoSubmitForm(document.body);

    if (!form) {
      throw new Error('SSO completion form vanished after rendering');
    }

    form.submit();

    return;
  }

  const redirectUrl = extractRedirectUrl(parsed);

  if (redirectUrl) {
    window.location.assign(redirectUrl);

    return;
  }

  // Don't log the response body: it can carry SAMLResponse / code / id_token.
  console.error('SSO completion response not recognized (no form or redirect)');
  throw new Error('SSO completion form not found in response');
}

/**
 * Loads and submits an SSO completion form (SAML or OAuth form_post).
 * The auth_id is read from the pending_auth_session httponly cookie by the backend.
 *
 * For split (SSK) flows, returns the partial-signing data instead of submitting;
 * the caller navigates to the matching complete-signing page.
 *
 * @returns the split-SAML or split-OIDC completion data, or void for a normal
 *   SAML / OAuth form_post (which is auto-submitted here)
 * @throws Error if the form cannot be loaded or submitted
 */
export async function submitSsoCompletion(): Promise<SsoCompletion | void> {
  try {
    const htmlContent = await pendingAuthService.loadAuth();
    console.info('SSO completion HTML loaded, length:', htmlContent.length);

    const oidcSskData = extractOidcSskData(htmlContent);

    if (oidcSskData) {
      console.info(
        'Split OIDC response detected, deferring to complete-signing'
      );

      return { kind: 'split_oidc', data: oidcSskData };
    }

    const splitSamlData = extractSplitSamlData(htmlContent);

    if (splitSamlData) {
      console.info(
        'Split SAML response detected, deferring to complete-signing'
      );

      return { kind: 'split_saml', data: splitSamlData };
    }

    executeCompletionHtml(htmlContent);
  } catch (error) {
    console.error('Failed to load or submit SSO completion response:', error);
    throw error;
  }
}
